import { createContext, useCallback, useMemo } from 'react';
import { isUserWithData } from '@/utils/isUserWithData';
import { signInPost, signOutPost, userGet } from '@/services/api/requests/user';
import { TUser } from '@/types/User.types';
import roles from '@/constants/roles';
import { myOrganizationGet } from '@/services/api/requests/organization';
import { lastScoresGet } from '@/services/api/requests/result';
import { useAppStore } from '@/store/useAppStore';
import { acceptedDocumentsGet } from '@/services/api/requests/legal';
import { TLegalDocumentAcceptanceStatus } from '@/types/Legal.types';
import { THealthDomainScores } from '@/types/Health.types';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { getCurrentOrganizationContext } from '@/services/api/requests/super-admin';
import useToast from '@/hooks/useToast';
import { resetUserSession } from '@/services/resetUserSession';

export type TAuthContext = {
  user: TUser | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isPeerSupport: boolean;
  isContentCreator: boolean;
  isUserWithDataNotSet: boolean;
  isUserWithDataSet: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<TUser>;
};

export const AuthContext = createContext<TAuthContext | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, setAppStoreOnSignIn, setLegalAcceptanceStatuses, setUser } = useAppStore();

  const toast = useToast();

  const { handleSignIn: handleSignInTelemetry, handleSignOut: handleSignOutTelemetry } = useTelemetry();

  const isAuthenticated = !!user;
  const isAdmin = !!user?.roles.includes(roles.orgAdmin);
  const isSuperAdmin = !!user?.roles.includes(roles.superAdmin);
  const isPeerSupport = !!user?.roles.includes(roles.peerSupport);
  const isContentCreator = !!user?.roles.includes(roles.contentCreator);

  const isUserWithDataSet = isUserWithData(user);
  const isUserWithDataNotSet = !isUserWithDataSet && !isAdmin;

  const handleUserDataFlow = async (userData: TUser) => {
    const requests = [];

    const allowedRoles = [roles.orgAdmin, roles.orgEmployee, roles.superAdmin, roles.contentCreator];
    const userRoles = userData.roles;

    const isSuperAdmin = userData.roles.includes(roles.superAdmin);
    if (isSuperAdmin) {
      const superAdminContext = await getCurrentOrganizationContext();
      if (!superAdminContext) {
        toast({
          status: 'error',
          title: 'Wrong Platform.',
          description: 'Please proceed to backoffice.myomnia.health',
        });

        signOut();

        throw Error('Wrong platform.\nPlease proceed to backoffice.myomnia.health');
      }
      if (isSuperAdmin) {
        userData.app_metadata = {
          ...userData.app_metadata,
          primaryOrganizationId: superAdminContext.organizationId,
          ...superAdminContext,
        };
      }
    }

    const userAllowedToSignIn = allowedRoles.some((role) => userRoles.includes(role));

    if (!userAllowedToSignIn) {
      signOut();
      throw Error('User does not have permission to sign in.');
    }

    requests.push(lastScoresGet());
    requests.push(acceptedDocumentsGet());

    const responses = await Promise.all(requests);

    const scoresResponse = responses[0] as THealthDomainScores;
    const eulaResponse = responses[1] as TLegalDocumentAcceptanceStatus[];

    let organizationResponse = null;

    const isAdmin = userData.roles.includes(roles.orgAdmin);
    if (isAdmin || isSuperAdmin) {
      organizationResponse = await myOrganizationGet();
    }

    setAppStoreOnSignIn(userData, scoresResponse, eulaResponse, organizationResponse);

    handleSignInTelemetry(userData.user_id);
  };

  const signIn = useCallback(async (email: string, password: string) => {
    const userData = await signInPost(email, password);

    await handleUserDataFlow(userData);
  }, []);

  const refreshUserData = useCallback(async () => {
    const refreshedUser = await userGet();
    const legalStatuses = await acceptedDocumentsGet();

    setUser(refreshedUser);
    setLegalAcceptanceStatuses(legalStatuses);

    return refreshedUser;
  }, [setLegalAcceptanceStatuses, setUser]);

  const signOut = useCallback(async () => {
    handleSignOutTelemetry(user?.user_id ?? 'id-not-provided');

    // Clear user state synchronously before navigation or another login can render.
    resetUserSession();

    await signOutPost();
  }, [handleSignOutTelemetry, user?.user_id]);

  const auth = useMemo(
    () => ({
      isAuthenticated,
      user,
      isUserWithDataNotSet,
      isUserWithDataSet,
      isAdmin,
      isPeerSupport,
      isContentCreator,
      isSuperAdmin,
      signIn,
      signOut,
      refreshUserData,
    }),
    [
      isAuthenticated,
      user,
      signIn,
      signOut,
      isUserWithDataNotSet,
      isUserWithDataSet,
      isAdmin,
      isPeerSupport,
      isContentCreator,
      isSuperAdmin,
      refreshUserData,
    ],
  );

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
